import { useCallback, useEffect, useMemo } from 'react'
import { ListboxGroup } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import { Installation } from '@/features/suppliers/hooks/installations'
import { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import type { ImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import {
  getBatchRemoveActionsAvailability,
  getNoEmissionsReportsAvailableWarningKey,
  type GetAggregatedCategoryForCnCode
} from '@/shared/utils/batchUpdateSelectionRules'
import { useSupplierEmissionsEligibility } from '../../../../hooks/useSupplierEmissionsEligibility'
import type { BulkUpdateSupplierEmissionsFormData } from './BulkUpdateSupplierEmissionsForm.schema'
import { EMPTY_VALUE } from './BulkUpdateSupplierEmissionsForm.schema'

export type EligibilityMessages = {
  emissionsTooltipKey: string | null
  noReportsAvailableWarningKey: string | null
}

type BulkUpdateSupplierEmissionsFormProps = {
  data: BulkUpdateSupplierEmissionsFormData
  onChange: (data: BulkUpdateSupplierEmissionsFormData) => void
  selectedGoods: ImportedGood[]
  partnerships: Partnership[]
  installations: Installation[]
  emissionsReports: SupplierEmissionsReport[]
  getAggregatedCategoryForCnCode: GetAggregatedCategoryForCnCode
  onEligibilityMessagesChange?: (messages: EligibilityMessages) => void
  cbamReportYear?: number
}

export default function BulkUpdateSupplierEmissionsForm({
  data,
  onChange,
  selectedGoods,
  partnerships,
  installations,
  emissionsReports,
  getAggregatedCategoryForCnCode,
  onEligibilityMessagesChange,
  cbamReportYear
}: BulkUpdateSupplierEmissionsFormProps) {
  const { t } = useI18n()

  const selectedForEligibility = useMemo(
    () => selectedGoods.map(good => ({ country: good.country, cnCode: good.cnCode })),
    [selectedGoods]
  )
  const removeActionsAvailability = useMemo(
    () => getBatchRemoveActionsAvailability(selectedGoods),
    [selectedGoods]
  )

  const { installationEligibility, emissionsEligibility } = useSupplierEmissionsEligibility({
    selectedGoods: selectedForEligibility,
    installations,
    emissionsReports,
    getAggregatedCategoryForCnCode,
    selectedInstallationId: data.installationId || null,
    cbamReportYear
  })

  const handleSupplierInstallationChange = useCallback(
    (value: string) => {
      if (value === EMPTY_VALUE) {
        onChange({
          ...data,
          partnershipId: EMPTY_VALUE,
          installationId: EMPTY_VALUE,
          supplierEmissionsReportId: ''
        })
        return
      }
      const installation = installations.find(inst => inst.id === value)
      if (installation) {
        onChange({
          ...data,
          partnershipId: installation.partnershipId,
          installationId: value,
          supplierEmissionsReportId: ''
        })
      }
    },
    [onChange, data, installations]
  )

  const handleEmissionsReportChange = useCallback(
    (value: string) => {
      onChange({ ...data, supplierEmissionsReportId: value })
    },
    [onChange, data]
  )

  const offPlatformPartnershipIds = useMemo(
    () =>
      new Set(
        partnerships
          .filter(partnership => partnership.isOffPlatform)
          .map(partnership => partnership.id)
      ),
    [partnerships]
  )

  const supplierInstallationOptions = useMemo(() => {
    const eligibleSet = new Set(installationEligibility.eligibleInstallationIds)
    const eligibleInstallations = installations.filter(
      inst => eligibleSet.has(inst.id) && offPlatformPartnershipIds.has(inst.partnershipId)
    )
    const partnershipById = new Map(partnerships.map(partnership => [partnership.id, partnership]))
    const removeOption = removeActionsAvailability.canRemoveSupplierInstallation
      ? [{ value: EMPTY_VALUE, label: t('cbamReporting:bulkUpdate.removeSupplierInstallation') }]
      : []
    return [
      ...removeOption,
      ...eligibleInstallations.map(inst => {
        const partnership = partnershipById.get(inst.partnershipId)
        const label = partnership ? `${partnership.name} – ${inst.name}` : inst.name
        return { value: inst.id, label }
      })
    ]
  }, [
    installations,
    partnerships,
    offPlatformPartnershipIds,
    installationEligibility.eligibleInstallationIds,
    removeActionsAvailability.canRemoveSupplierInstallation,
    t
  ])

  const emissionsReportOptions = useMemo(() => {
    const eligibleSet = new Set(emissionsEligibility.eligibleReportIds)
    const filtered = emissionsReports.filter(report => eligibleSet.has(report.id))
    const removeOption = removeActionsAvailability.canRemoveEmissionsReport
      ? [{ value: EMPTY_VALUE, label: t('cbamReporting:bulkUpdate.removeEmissionsReport') }]
      : []
    return [
      ...removeOption,
      ...filtered.map(report => ({
        value: report.id,
        label:
          report.goods.map(good => good.goodsDescription).join(', ') ||
          `Report ${report.id.slice(0, 8)}`
      }))
    ]
  }, [
    emissionsReports,
    emissionsEligibility.eligibleReportIds,
    removeActionsAvailability.canRemoveEmissionsReport,
    t
  ])

  // Check if no emissions reports are available for the selected installation
  const hasNoReportsAvailable = useMemo(() => {
    if (!data.installationId || data.installationId === EMPTY_VALUE) {
      return false
    }
    const eligibleSet = new Set(emissionsEligibility.eligibleReportIds)
    const filtered = emissionsReports.filter(report => eligibleSet.has(report.id))
    return filtered.length === 0
  }, [data.installationId, emissionsReports, emissionsEligibility.eligibleReportIds])

  useEffect(() => {
    onEligibilityMessagesChange?.({
      emissionsTooltipKey: emissionsEligibility.tooltipKey,
      noReportsAvailableWarningKey: hasNoReportsAvailable
        ? getNoEmissionsReportsAvailableWarningKey(cbamReportYear)
        : null
    })
  }, [
    onEligibilityMessagesChange,
    emissionsEligibility.tooltipKey,
    hasNoReportsAvailable,
    cbamReportYear
  ])

  const isSupplierInstallationDisabled = !!installationEligibility.ineligibilityReason

  const isEmissionsReportDisabled =
    !data.installationId || data.installationId === EMPTY_VALUE || !emissionsEligibility.canSelect

  return (
    <div className="space-y-4">
      <ListboxGroup
        label={t('cbamReporting:bulkUpdate.fields.supplierAndInstallation')}
        value={data.installationId}
        placeholder={t('cbamReporting:bulkUpdate.noChange')}
        options={supplierInstallationOptions}
        onChange={handleSupplierInstallationChange}
        disabled={isSupplierInstallationDisabled}
      />
      <ListboxGroup
        label={t('cbamReporting:bulkUpdate.fields.emissionsReport')}
        value={data.supplierEmissionsReportId}
        placeholder={t('cbamReporting:bulkUpdate.noChange')}
        options={emissionsReportOptions}
        onChange={handleEmissionsReportChange}
        disabled={isEmissionsReportDisabled}
      />
    </div>
  )
}
